import type { ActorRoleName, MeResponse } from '@league/shared/contract';

import { apiFetch, apiPost } from './api';

export interface Actor {
  id: string;
  /* `instructor` (2026-10-16): runs session nights for a director, on the director's screens. */
  role: ActorRoleName;
  label: string;
}

export interface SignInOptions {
  password: boolean;
  google: boolean;
  minPasswordLength: number;
  codeExpiryMinutes: number;
  linkExpiryMinutes: number;
}

/** What the screen falls back to if the API cannot be reached — the shape, not a promise. */
export const DEFAULT_SIGN_IN_OPTIONS: SignInOptions = {
  password: true,
  google: false,
  minPasswordLength: 10,
  codeExpiryMinutes: 10,
  linkExpiryMinutes: 60,
};

/**
 * `playsToo`: a director (or the commissioner) with a player record of her own. She gets "My games"
 * in her menu (owner, 2026-09-26). Always true for a player.
 */
export function getMe(): Promise<MeResponse> {
  return apiFetch<MeResponse>('/api/me');
}

/** What the API is actually able to offer, so the screen never shows a button that can't work. */
export function getSignInOptions(): Promise<SignInOptions> {
  return apiFetch<SignInOptions>('/api/sign-in/options');
}

/**
 * Step one. A correct password on an account with a second factor does NOT return a session — it
 * returns `twoFactorRedirect`, and the caller has to go on to the code. Reading that flag rather
 * than the role is deliberate: the server decides who needs a second factor, and a copy of that
 * rule here is a copy that can disagree with it.
 */
export function signInWithPassword(
  email: string,
  password: string,
): Promise<{ twoFactorRedirect?: boolean }> {
  return apiPost<{ twoFactorRedirect?: boolean }>('/api/auth/sign-in/email', { email, password });
}

export function sendSignInCode(): Promise<unknown> {
  return apiPost('/api/auth/two-factor/send-otp', {});
}

/**
 * No email address here — the challenge is held in a cookie, so the code is all that's needed.
 * Always trusts the device: it skips the code for thirty days (Courtney, 2026-10-23).
 */
export function verifySignInCode(code: string): Promise<unknown> {
  return apiPost('/api/auth/two-factor/verify-otp', { code, trustDevice: true });
}

/**
 * Asks for a set-password link — claiming a new account and resetting a lost password are the same
 * request, and which of the two it is gets decided in the email.
 *
 * Succeeds identically whether or not the address is on file. An unknown one must not be
 * distinguishable, or this form becomes a way to find out who directs leagues.
 */
export function requestSetPasswordLink(email: string): Promise<{ expiresInMinutes: number }> {
  return apiPost<{ expiresInMinutes: number }>('/api/sign-in/set-password', { email });
}

export function setPassword(token: string, newPassword: string): Promise<unknown> {
  return apiPost('/api/auth/reset-password', { token, newPassword });
}

export const GOOGLE_SIGN_IN_URL = '/api/sign-in/google';

export function signOut(): Promise<unknown> {
  return apiPost('/api/auth/sign-out', {});
}

/** Where a signed-in actor's app lives. The server says the same thing; this is the client half. */
export function homePathFor(actor: Actor): string {
  if (actor.role === 'commissioner') return '/hq';
  if (actor.role === 'director' || actor.role === 'instructor') return '/director';
  return '/my';
}
